import { PublicKey, SystemProgram, Transaction, VersionedTransaction } from "@solana/web3.js";
import bs58 from "bs58";

export const SIGNATURE_BYTES = 64;

export const messageOf = (raw: Uint8Array): Uint8Array =>
  raw.subarray(1 + (raw[0] ?? 0) * SIGNATURE_BYTES);

export const isVersioned = (raw: Uint8Array): boolean => ((messageOf(raw)[0] ?? 0) & 0x80) !== 0;

export const bytesOf = (base64: string): Uint8Array =>
  Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));

export const b64 = (raw: Uint8Array): string => {
  let binary = "";
  for (const byte of raw) binary += String.fromCharCode(byte);
  return btoa(binary);
};

export const decode = (raw: Uint8Array): Transaction | VersionedTransaction =>
  isVersioned(raw) ? VersionedTransaction.deserialize(raw) : Transaction.from(raw);

export const signerSlotOf = (raw: Uint8Array, signer: string): number => {
  const parsed = decode(raw);
  return parsed instanceof VersionedTransaction
    ? parsed.message.staticAccountKeys.findIndex((key) => key.toBase58() === signer)
    : parsed.signatures.findIndex((pair) => pair.publicKey.toBase58() === signer);
};

export const isSignerOn = (raw: Uint8Array, signer: string): boolean => {
  try {
    return signerSlotOf(raw, signer) >= 0;
  } catch {
    return false;
  }
};

const signatureAt = (raw: Uint8Array, slot: number): Uint8Array =>
  raw.subarray(1 + slot * SIGNATURE_BYTES, 1 + (slot + 1) * SIGNATURE_BYTES);

const verifiesOver = async (
  message: Uint8Array,
  signature: Uint8Array,
  signer: string,
): Promise<boolean | null> => {
  try {
    const key = await crypto.subtle.importKey(
      "raw",
      bs58.decode(signer) as Uint8Array<ArrayBuffer>,
      { name: "Ed25519" },
      false,
      ["verify"],
    );
    return await crypto.subtle.verify(
      { name: "Ed25519" },
      key,
      signature as Uint8Array<ArrayBuffer>,
      message as Uint8Array<ArrayBuffer>,
    );
  } catch {
    return null;
  }
};

const keysOf = (message: Uint8Array): string[] => {
  let at = ((message[0] ?? 0) & 0x80) !== 0 ? 4 : 3;
  let count = 0;
  for (let shift = 0; ; shift += 7) {
    const byte = message[at] as number;
    at += 1;
    count |= (byte & 0x7f) << shift;
    if ((byte & 0x80) === 0) break;
  }
  const keys: string[] = [];
  for (let index = 0; index < count && at + 32 <= message.length; index += 1, at += 32) {
    keys.push(bs58.encode(message.subarray(at, at + 32)));
  }
  return keys;
};

export const walletReport = async (
  ours: Uint8Array,
  theirs: Uint8Array,
  signer: string,
  signature: Uint8Array,
): Promise<string> => {
  const ourMessage = messageOf(ours);
  const theirMessage = messageOf(theirs);

  const held = new Set(keysOf(ourMessage));
  const added = keysOf(theirMessage).filter((key) => !held.has(key));

  let where = "identical";
  if (ourMessage.length !== theirMessage.length) {
    where = `lengths ${theirMessage.length} vs our ${ourMessage.length}`;
  } else {
    const at = ourMessage.findIndex((byte, index) => byte !== theirMessage[index]);
    if (at >= 0) {
      where = `byte ${at} is 0x${(theirMessage[at] as number).toString(16)} vs our 0x${(ourMessage[at] as number).toString(16)}`;
    }
  }

  const overOurs = await verifiesOver(ourMessage, signature, signer);
  const overTheirs = await verifiesOver(theirMessage, signature, signer);
  return `wallet message ${where}; verifies over ours=${overOurs} theirs=${overTheirs}; added ${added.join(" ") || "nothing"}`;
};

export const signedForSubmission = async (
  originalB64: string,
  theirs: Uint8Array,
  signer: string,
  note?: (line: string) => void,
): Promise<string> => {
  const ours = bytesOf(originalB64);

  const theirSlot = signerSlotOf(theirs, signer);
  if (theirSlot < 0) throw new Error(`the wallet returned no signature for ${signer.slice(0, 8)}…`);
  const signature = signatureAt(theirs, theirSlot);

  if (signature.length !== SIGNATURE_BYTES || signature.every((byte) => byte === 0)) {
    throw new Error(
      `the wallet returned an EMPTY signature for ${signer.slice(0, 8)}… — it did not sign`,
    );
  }

  note?.(await walletReport(ours, theirs, signer, signature));

  const ourSlot = signerSlotOf(ours, signer);
  if (ourSlot < 0) throw new Error(`${signer.slice(0, 8)}… is not a signer on this transaction`);
  const spliced = Uint8Array.from(ours);
  spliced.set(signature, 1 + ourSlot * SIGNATURE_BYTES);

  if ((await verifiesOver(messageOf(spliced), signature, signer)) !== false) return b64(spliced);

  if ((await verifiesOver(messageOf(theirs), signature, signer)) === true) {
    note?.(
      "the wallet rewrote the transaction — sending ITS bytes for the server to check against ours",
    );
    return b64(theirs);
  }

  throw new Error(
    "the wallet's signature verifies against neither our transaction nor its own — it signed with a different key",
  );
};

export const throwaway = (alongside: Uint8Array, owner: string): Uint8Array => {
  const parsed = decode(alongside);
  const blockhash =
    parsed instanceof VersionedTransaction
      ? parsed.message.recentBlockhash
      : (parsed.recentBlockhash ?? "");

  const self = new PublicKey(owner);
  const decoy = new Transaction();
  decoy.add(SystemProgram.transfer({ fromPubkey: self, toPubkey: self, lamports: 0 }));
  decoy.recentBlockhash = blockhash;
  decoy.feePayer = self;
  return new Uint8Array(decoy.serialize({ requireAllSignatures: false, verifySignatures: false }));
};
